import { motion } from 'framer-motion';
import { Menu, X } from 'lucide-react';
import { useState } from 'react';

import { AppleMark } from './icons/AppleMark';
import appIcon from '../../../application/assets/images/icon.png';

import { APP_STORE_URL, LINKS } from '@/lib/constants';
import type { NavSectionId, Route } from '@/lib/constants';
import { cn } from '@/lib/utils';

interface NavigationProps {
  isHome: boolean;
  activeSection: NavSectionId | null;
  onNavigate: (route: Route) => void;
  onSectionClick: (id: NavSectionId) => void;
  onResetSection: () => void;
  mobileMenuTrigger: React.ReactNode;
}

const navLinks = [
  { label: 'Manifesto', href: '#manifesto', id: 'manifesto' as const },
  { label: 'Product', href: '#product', id: 'product' as const },
  { label: 'Platforms', href: '#platforms', id: 'platforms' as const },
  { label: 'Imaginer', href: '#imaginer', id: 'imaginer' as const },
  { label: 'Docs', href: LINKS.docs, external: true },
];

export function Navigation({
  isHome,
  activeSection,
  onNavigate,
  onSectionClick,
  onResetSection,
}: NavigationProps) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  return (
    <motion.header
      initial={{ y: -20, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1], delay: 0.1 }}
      className="fixed top-0 left-0 right-0 z-50 bg-black/80 backdrop-blur-md border-b border-white/10"
    >
      <nav className="container mx-auto px-5 lg:px-12 h-16 flex items-center justify-between">
        {/* Logo */}
        <a
          href={isHome ? '#top' : '/'}
          className="flex items-center gap-2 text-white font-semibold"
          onClick={(e) => {
            if (!isHome) {
              e.preventDefault();
              onNavigate('/');
              return;
            }
            onResetSection();
          }}
        >
          <img src={appIcon} alt="Michelangelo" className="w-7 h-7 rounded-lg" />
          <span>Michelangelo</span>
        </a>

        {/* Desktop Navigation */}
        <div className="hidden md:flex items-center gap-8">
          {isHome ? (
            navLinks.map((link) => (
              <a
                key={link.label}
                href={link.href}
                target={link.external ? '_blank' : undefined}
                rel={link.external ? 'noreferrer' : undefined}
                onClick={() => {
                  if (!link.external && link.id) {
                    onSectionClick(link.id);
                  }
                }}
                className={cn(
                  'text-sm font-medium transition-colors duration-200',
                  activeSection === link.id ? 'text-white' : 'text-white/70 hover:text-white',
                )}
              >
                {link.label}
              </a>
            ))
          ) : (
            <>
              <a
                href="/"
                onClick={(e) => {
                  e.preventDefault();
                  onNavigate('/');
                }}
                className="text-sm font-medium text-white/70 hover:text-white transition-colors duration-200"
              >
                Home
              </a>
              <a
                href={LINKS.docs}
                target="_blank"
                rel="noreferrer"
                className="text-sm font-medium text-white/70 hover:text-white transition-colors duration-200"
              >
                Docs
              </a>
            </>
          )}
        </div>

        {/* CTA Button */}
        <a
          href={APP_STORE_URL}
          target="_blank"
          rel="noreferrer"
          className="hidden md:flex items-center gap-2 bg-white text-black px-4 py-2 rounded-lg text-sm font-medium hover:bg-white/90 transition-all duration-200 hover:scale-[1.02]"
        >
          <AppleMark className="w-4 h-4" />
          <span>Download for iOS</span>
        </a>

        {/* Mobile Menu Button */}
        <button
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          className="md:hidden text-white p-2"
          aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
        >
          {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </nav>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="md:hidden bg-black/95 backdrop-blur-md border-t border-white/10"
        >
          <div className="px-5 py-4 flex flex-col gap-4">
            {isHome ? (
              navLinks.map((link) => (
                <a
                  key={link.label}
                  href={link.href}
                  target={link.external ? '_blank' : undefined}
                  rel={link.external ? 'noreferrer' : undefined}
                  onClick={() => {
                    setMobileMenuOpen(false);
                    if (!link.external && link.id) {
                      onSectionClick(link.id);
                    }
                  }}
                  className={cn(
                    'text-sm font-medium transition-colors duration-200',
                    activeSection === link.id ? 'text-white' : 'text-white/70 hover:text-white',
                  )}
                >
                  {link.label}
                </a>
              ))
            ) : (
              <a
                href="/"
                onClick={(e) => {
                  e.preventDefault();
                  onNavigate('/');
                  setMobileMenuOpen(false);
                }}
                className="text-sm font-medium text-white/70 hover:text-white transition-colors duration-200"
              >
                Home
              </a>
            )}
            <a
              href={APP_STORE_URL}
              target="_blank"
              rel="noreferrer"
              className="flex items-center justify-center gap-2 bg-white text-black px-4 py-2 rounded-lg text-sm font-medium mt-2"
            >
              <AppleMark className="w-4 h-4" />
              <span>Download for iOS</span>
            </a>
          </div>
        </motion.div>
      )}
    </motion.header>
  );
}
